/** M10.2: account & session API (see apps/api/src/routes/auth.ts, account.ts). */
import { apiFetch } from './client.ts';
import { powHeaders } from './pow.ts';
import { setSession, clearSession, csrfToken } from './session.ts';
import type { AuthResponse, RegisterResponse, AccountProfile, SessionListResponse, LegalConsentState } from '@galaxis/shared';

const post = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body), auth: false });

/**
 * A proof-of-work call. If the server answers 428 (challenge unusable: the network switched its outbound address,
 * the challenge expired while the form was open, ...) it is retried ONCE with a fresh challenge; a second 428 gets a
 * plain-language message instead of the API's technical one.
 */
async function withPow<T>(call: (headers: Record<string, string>) => Promise<T>): Promise<T> {
  try {
    return await call(await powHeaders());
  } catch (e) {
    if ((e as { statusCode?: number }).statusCode !== 428) throw e;
  }
  try {
    return await call(await powHeaders());
  } catch (e) {
    if ((e as { statusCode?: number }).statusCode === 428) throw new Error('Az automatikus ellenőrzés nem sikerült. Várj egy pillanatot, és próbáld újra.');
    throw e;
  }
}

export async function login(identifier: string, password: string): Promise<AuthResponse> {
  const res = await apiFetch<AuthResponse>('/auth/login', post({ identifier, password }));
  setSession(res);
  return res;
}

export async function demoLogin(): Promise<AuthResponse> {
  const res = await apiFetch<AuthResponse>('/auth/demo', post({}));
  setSession(res);
  return res;
}

/** Guest play: a session without registration; the civilization survives the later upgrade. */
export async function guestLogin(): Promise<AuthResponse> {
  const res = await withPow(headers => apiFetch<AuthResponse>('/auth/guest', { ...post({}), headers }));
  setSession(res);
  return res;
}

export function upgradeGuest(input: { username?: string; email: string; password: string; passwordConfirm: string; acceptLegal: true; legalVersions?: { terms: string; privacy: string } }): Promise<{ ok: true; message: string }> {
  return apiFetch('/account/upgrade', { method: 'POST', body: JSON.stringify(input) });
}

export async function register(input: { username: string; email: string; password: string; passwordConfirm: string; inviteCode?: string; acceptLegal: true; legalVersions?: { terms: string; privacy: string } }): Promise<RegisterResponse> {
  return withPow(headers => apiFetch<RegisterResponse>('/auth/register', { ...post(input), headers }));
}

/** M10.6: open | invite (closed Alpha) | closed (paused; existing players can still sign in). */
export type RegistrationMode = 'open' | 'invite' | 'closed';
export async function fetchRegistrationMode(): Promise<RegistrationMode> {
  return (await apiFetch<{ mode: RegistrationMode }>('/auth/registration', { auth: false })).mode;
}

export async function fetchGuestAvailable(): Promise<boolean> {
  return (await apiFetch<{ mode: RegistrationMode; guest?: boolean }>('/auth/registration', { auth: false })).guest === true;
}

/** The link signs the player in (kind 'verify'); an e-mail change ends every session instead (kind 'email_change'). */
export async function verifyEmail(token: string): Promise<{ ok: true; kind: 'verify' | 'email_change'; message: string; session?: AuthResponse }> {
  const res = await apiFetch<{ ok: true; kind: 'verify' | 'email_change'; message: string; session?: AuthResponse }>('/auth/verify-email', post({ token }));
  if (res.session) setSession(res.session);
  return res;
}

export async function resendVerification(email: string): Promise<{ ok: true; message: string }> {
  return withPow(headers => apiFetch('/auth/resend-verification', { ...post({ email }), headers }));
}

export async function forgotPassword(email: string): Promise<{ ok: true; message: string }> {
  return withPow(headers => apiFetch('/auth/forgot-password', { ...post({ email }), headers }));
}

export function resetPassword(token: string, password: string, passwordConfirm: string): Promise<{ ok: true; message: string }> {
  return apiFetch('/auth/reset-password', post({ token, password, passwordConfirm }));
}

/** Ends this device's session (cookie + CSRF), whatever state the access token is in. */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': csrfToken() } });
  } finally {
    clearSession();
  }
}

export async function logoutAll(): Promise<void> {
  await apiFetch('/auth/logout-all', { method: 'POST' });
  clearSession();
}

export const fetchAccount = () => apiFetch<AccountProfile>('/account');
export const fetchSessions = () => apiFetch<SessionListResponse>('/account/sessions');
export const revokeSession = (id: string) => apiFetch<void>(`/account/sessions/${id}`, { method: 'DELETE' });
export const changePassword = (currentPassword: string, password: string, passwordConfirm: string) =>
  apiFetch<{ ok: true; message: string }>('/account/password', { method: 'POST', body: JSON.stringify({ currentPassword, password, passwordConfirm }) });
export const setAccountEmail = (email: string, currentPassword: string) =>
  apiFetch<{ ok: true; message: string }>('/account/email', { method: 'POST', body: JSON.stringify({ email, currentPassword }) });
export const changeEmail = (newEmail: string, currentPassword: string) =>
  apiFetch<{ ok: true; message: string }>('/account/email/change', { method: 'POST', body: JSON.stringify({ newEmail, currentPassword }) });

/** GDPR export: the account's own data as a JSON file download. */
export async function downloadAccountData(): Promise<void> {
  const data = await apiFetch<unknown>('/account/export');
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `parallax-gate-adataim-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function deleteAccount(currentPassword: string | undefined, confirm: string): Promise<{ ok: true; message: string }> {
  const res = await apiFetch<{ ok: true; message: string }>('/account', { method: 'DELETE', body: JSON.stringify({ ...(currentPassword ? { currentPassword } : {}), confirm }) });
  clearSession();
  return res;
}

/** Legal pages: the player's consent state, and re-acceptance of the current Terms + Privacy notice. */
export const fetchLegalState = () => apiFetch<LegalConsentState>('/account/legal');
export const acceptLegal = (versions: { terms: string; privacy: string }) =>
  apiFetch<LegalConsentState>('/account/legal/accept', { method: 'POST', body: JSON.stringify({ accept: true, versions }) });
